import { type JSX, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import { type NextPage } from 'next'
import { type AppProps } from 'next/app'
import Head from 'next/head'
import styled from 'styled-components'
import { transformRoutesToTitle } from '@/features/shared/utils/transformRoutesToTitle'
import '@/features/shared/styles/global.css'
import { routes } from '@shared/constants/routes'
import { useQueryUser } from '@/features/account/services/User/queries'
import { type CombinedPageProps } from '@/features/shared/utils/setPageProps'
import { DialogContainer } from '@/features/shared/components/DialogContainer'
import { RootProviders } from '@/RootProviders'
import { SegwayApp } from '@/SegwayApp'
import { CoreProviders } from '@/CoreProviders'

const DynamicSimpleHeader = dynamic(
  () => import('@/features/shared/components/Headers/SimpleHeader').then((m) => m.SimpleHeader),
  { ssr: false },
)
const DynamicErrorPage = dynamic(
  () => import('@/features/shared/components/ErrorPage').then((m) => m.ErrorPage),
  { ssr: false },
)
const DynamicErrorPageBase = dynamic(
  () => import('@/features/shared/components/ErrorPage').then((m) => m.ErrorPageBase),
  { ssr: false },
)

export type NextPageWithLayout<P extends object = CombinedPageProps, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: JSX.Element, props: AppLayoutProps) => ReactNode
}
export type AppLayoutProps = Pick<AppProps, 'router'>
type AppPropsWithLayout = AppProps<CombinedPageProps> & {
  Component: NextPageWithLayout
}
function StyledApp({ Component, pageProps, router }: AppPropsWithLayout) {
  const { routeData } = pageProps
  const { error: userError } = useQueryUser()
  const title = transformRoutesToTitle(router.pathname, routeData)

  const isPreviewPage =
    router.route.includes(routes.CMS_MODULE.url) ||
    router.route.includes(routes.CMS_PREVIEW_PAGE.url) ||
    router.route.includes(routes.CMS_BULK_CONTENT_PREVIEW.url)

  if (isPreviewPage) {
    return (
      <>
        {/* oxlint-disable-next-line next/no-duplicate-head -- false positive by oxlint */}
        <Head>
          <title>Preview</title>
        </Head>
        <Component {...pageProps} />
      </>
    )
  }
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page)

  const pageContent = (
    <BodyContainer id="main-content">
      {pageProps.statusCode ? (
        <DynamicErrorPage statusCode={pageProps.statusCode} />
      ) : (
        <Component {...pageProps} />
      )}
    </BodyContainer>
  )
  return (
    <>
      <Head>
        <title>{title}</title>
        {/* Prevent specific pages from being indexed */}
        {!!routeData?.noIndex && (
          <>
            <meta name="robots" content="noindex" />
            <meta name="googlebot" content="noindex" />
          </>
        )}
      </Head>
      <SegwayApp>
        {userError ? (
          <>
            <DynamicSimpleHeader />
            <DynamicErrorPageBase />
          </>
        ) : (
          getLayout(pageContent, { router })
        )}
      </SegwayApp>
    </>
  )
}

const BodyContainer = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.color.background.base.neutral.default};
`

export default function App(props: AppProps<CombinedPageProps>) {
  const { pageProps } = props
  const { reactQueryState, routeData } = pageProps

  return (
    <RootProviders>
      <CoreProviders routeData={routeData} dehydratedState={reactQueryState}>
        <DialogContainer>
          <StyledApp {...props} />
        </DialogContainer>
      </CoreProviders>
    </RootProviders>
  )
}
